import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/lib/lang";
import { useCases } from "@/hooks/queries";
import { Button } from "@/components/ui/button";
import CaseCreateDialog from "@/components/CaseCreateDialog";
import { Briefcase, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";

const PRACTICE_KEYS = {
  civil: "practice_civil",
  criminal: "practice_criminal",
};

const ONBOARDING_KEYS = {
  pending: "case_onboarding_pending",
  in_progress: "case_onboarding_in_progress",
  completed: "case_onboarding_completed",
  skipped: "case_onboarding_skipped",
};

export default function CasesPane({ documents = [] }) {
  const { t } = useLang();
  const navigate = useNavigate();
  const { data: cases = [], isLoading: loading } = useCases();
  const [createOpen, setCreateOpen] = useState(false);

  const handleCreated = (caseData) => {
    setCreateOpen(false);
    toast.success(t("case_created"));
    navigate(`/workspace/cases/${caseData.id}`);
  };

  return (
    <div className="h-full flex flex-col bg-white" data-testid="cases-pane">
      <div className="grid-border-b p-5 bg-slate-50/40 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Briefcase size={16} strokeWidth={1.6} className="text-slate-900" />
          <h2 className="font-serif-display text-lg text-slate-900">{t("nav_cases")}</h2>
        </div>
        <Button
          data-testid="case-create-btn"
          onClick={() => setCreateOpen(true)}
          className="rounded-none bg-slate-900 hover:bg-slate-800 text-white h-9 px-4 text-xs gap-2"
        >
          <Plus size={14} />
          {t("case_create")}
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400">
            <Loader2 size={18} className="animate-spin" />
          </div>
        ) : cases.length === 0 ? (
          <div className="text-center py-16 max-w-md mx-auto" data-testid="cases-empty">
            <Briefcase size={32} className="mx-auto mb-4 text-slate-300" strokeWidth={1.2} />
            <div className="text-sm font-medium text-slate-900 mb-1">{t("cases_empty_title")}</div>
            <div className="text-xs text-slate-500 mb-6">{t("cases_empty_sub")}</div>
            <Button
              onClick={() => setCreateOpen(true)}
              className="rounded-none bg-slate-900 hover:bg-slate-800 text-white"
            >
              {t("case_create")}
            </Button>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {cases.map((c) => (
              <button
                key={c.id}
                type="button"
                data-testid={`case-card-${c.id}`}
                onClick={() => navigate(`/workspace/cases/${c.id}`)}
                className="text-start grid-border bg-white p-5 hover:bg-slate-50 transition-colors"
              >
                <div className="font-serif-display text-lg text-slate-900 mb-2 line-clamp-2">
                  {c.title}
                </div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-3">
                  {t(PRACTICE_KEYS[c.practice_area] || c.practice_area)}
                </div>
                <div className="flex items-center justify-between text-xs text-slate-500">
                  <span>{t("case_docs_count").replace("{n}", String(c.doc_count || 0))}</span>
                  <span>{t(ONBOARDING_KEYS[c.onboarding_status] || c.onboarding_status)}</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <CaseCreateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        documents={documents}
        onCreated={handleCreated}
      />
    </div>
  );
}
